/*
 * What the tablet says about the connection.
 *
 * Two things a carer needs to be able to see without asking anybody: whether this tablet is
 * talking to the server, and whether anything recorded on it is still waiting to be sent. Both sit
 * at the top of every screen, because finding out at the end of a shift is too late.
 *
 * The third case is the one that matters most: a record the server refused. That is not a
 * connection problem and will not fix itself, so it is shown in red with what the server said, and
 * somebody has to look at it.
 */
import { useCallback, useEffect, useState } from 'react';
import { flush, forget, onOutboxChange, outbox, whoIsSignedIn } from './offline.ts';
import type { Outgoing } from './offline.ts';
import { Overlay } from './overlay.tsx';

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function ConnectionBanner() {
  const online = useOnline();
  const [items, setItems] = useState<Outgoing[]>([]);
  const [showing, setShowing] = useState(false);

  const look = useCallback(() => {
    void outbox().then(setItems);
  }, []);

  useEffect(() => {
    look();
    return onOutboxChange(look);
  }, [look]);

  const refused = items.filter((item) => item.refused);
  const waiting = items.filter((item) => !item.refused);

  if (online && !items.length) return null;

  return (
    <>
      {!online ? (
        <p className="notice warn">
          <strong>No connection.</strong> Carry on with the round — everything you record is saved on this
          tablet and sent when the signal comes back.
        </p>
      ) : null}

      {waiting.length ? (
        <p className="notice">
          {waiting.length} record{waiting.length === 1 ? '' : 's'} saved on this tablet, waiting to be sent.{' '}
          <button type="button" className="link" onClick={() => setShowing(true)}>See them</button>
          {online ? (
            <>
              {' · '}
              <button type="button" className="link" onClick={() => void flush()}>Try now</button>
            </>
          ) : null}
        </p>
      ) : null}

      {refused.length ? (
        <p className="error" role="alert">
          <strong>{refused.length} record{refused.length === 1 ? '' : 's'} could not be sent.</strong>{' '}
          <button type="button" className="link" onClick={() => setShowing(true)}>See what happened</button>
        </p>
      ) : null}

      {showing ? <OutboxDialog items={items} onClose={() => setShowing(false)} onChanged={look} /> : null}
    </>
  );
}

function OutboxDialog({
  items,
  onClose,
  onChanged,
}: {
  items: Outgoing[];
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Overlay onClose={onClose}>
      <div className="card wide">
        <div className="head">
          <h2>Waiting to be sent</h2>
          <button type="button" className="quiet" onClick={onClose}>Close</button>
        </div>
        <p className="muted">
          Recorded on this tablet. Nothing here is lost — it goes as soon as there is a connection.
        </p>

        <ul className="list">
          {items.map((item) => (
            <li key={item.id}>
              <div className={`row static column ${item.refused ? 'dose overdue' : ''}`}>
                <div className="row-line">
                  <span className="row-main">{item.what}</span>
                  <span className="muted small">{when(item.addedAt)}</span>
                </div>
                {item.refused ? (
                  <>
                    <p className="warn-text">{item.refused}</p>
                    <p className="muted small">
                      This will not send by itself. Check the resident's chart, and if the dose is already
                      recorded correctly you can take this off the list.
                    </p>
                    <div className="row-actions">
                      <button
                        type="button"
                        onClick={async () => {
                          await forget(item.id);
                          onChanged();
                        }}
                      >
                        I have checked the chart — remove it
                      </button>
                    </div>
                  </>
                ) : item.by !== whoIsSignedIn()?.id ? (
                  <p className="warn-text">
                    Recorded by {item.byName}. It goes under their login, so it will be sent when they next
                    sign in on this tablet.
                  </p>
                ) : (
                  <p className="muted small">
                    Waiting{item.tries ? ` · ${item.tries} attempt${item.tries === 1 ? '' : 's'} so far` : ''}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>

        {!items.length ? <p className="notice">Everything has been sent.</p> : null}
      </div>
    </Overlay>
  );
}

function when(at: number): string {
  const date = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
